import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { getApiConfiguration } from '../../config/apiConfig'

// Local type definitions
export interface Address {
  street?: string[]
  city?: string
  state?: string
  postal_code?: string
  country?: string
}

export interface Item {
  id: string
  href: string
  name?: string
  [key: string]: any
}

// HATEOAS-compliant clinic type with navigation links
export interface ClinicWithLinks {
  id: string
  href: string
  name: string
  address: Address
  // HATEOAS navigation links - these tell us WHERE to go, not HOW to construct URLs
  patients: { href: string }
  orders: { href: string }
  invoices: { href: string }
  pharmacies: { href: string }
  users: { href: string }
  formularies: { href: string }
  report: { href: string }
  dashboard: { href: string }
}

interface AuthState {
  isAuthenticated: boolean
  currentClinic: ClinicWithLinks | null  // Store the full clinic object, not just ID
  availableClinics: Item[]               // List of clinics user can access (from API)
  clinicsLoading: boolean
  intendedRoute: string | null           // Route to redirect to after login
  user: {
    id: string | null
    email: string | null
    name: string | null
  } | null
}

// Async thunk to load available clinics via HATEOAS
export const loadAvailableClinics = createAsyncThunk(
  'auth/loadAvailableClinics',
  async () => {
    const config = getApiConfiguration()
    
    // Get fresh token for this request
    const { default: authService } = await import('../../services/authService');
    const token = await authService.getValidAccessToken();
    
    // First, get the list of available clinics
    const searchResponse = await fetch(`${config.basePath}/clinic`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    })
    
    if (!searchResponse.ok) {
      throw new Error(`Failed to search clinics: ${searchResponse.statusText}`)
    }
    
    const searchData = await searchResponse.json()
    const clinics = searchData.items || []
    
    // If we have clinics, fetch the first clinic's details to get HATEOAS links
    let firstClinicWithLinks = null
    if (clinics.length > 0) {
      const firstClinic = clinics[0]
      
      // Fetch full clinic details with HATEOAS links via /clinic/{cid}
      const clinicResponse = await fetch(`${config.basePath}/clinic/${firstClinic.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })
      
      if (!clinicResponse.ok) {
        throw new Error(`Failed to fetch clinic details: ${clinicResponse.statusText}`)
      }
      
      const clinicDetails = await clinicResponse.json()
      
      firstClinicWithLinks = {
        id: clinicDetails.id!,
        href: clinicDetails.href!,
        name: clinicDetails.name!,
        address: clinicDetails.address!,
        // These are the HATEOAS navigation links from the individual clinic call
        patients: clinicDetails.patients!,
        orders: clinicDetails.orders!,
        invoices: clinicDetails.invoices!,
        pharmacies: clinicDetails.pharmacies!,
        users: clinicDetails.users!,
        formularies: clinicDetails.formularies!,
        report: clinicDetails.report!,
        dashboard: clinicDetails.dashboard!,
      }
    }
    
    return {
      availableClinics: clinics,
      firstClinicWithLinks
    }
  }
)

const initialState: AuthState = {
  isAuthenticated: false, // Will be set by real Cognito login
  currentClinic: null,    // Will be set when user selects a clinic
  availableClinics: [],   // Will be populated by loadAvailableClinics
  clinicsLoading: false,
  intendedRoute: null,    // Route to redirect to after login
  user: null              // Will be set by real Cognito login
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    login: (state, action: PayloadAction<{ user: any }>) => {
      state.isAuthenticated = true
      state.user = action.payload.user
      // Note: No clinic assignment here - user will select clinic after login
    },
    logout: (state) => {
      state.isAuthenticated = false
      state.currentClinic = null
      state.availableClinics = []
      state.intendedRoute = null // Clear intended route on logout
      state.user = null
    },
    selectClinic: (state, action: PayloadAction<ClinicWithLinks>) => {
      // This is the HATEOAS way - store the entire clinic object with its links
      state.currentClinic = action.payload
    },
    setIntendedRoute: (state, action: PayloadAction<string>) => {
      state.intendedRoute = action.payload
    },
    clearIntendedRoute: (state) => {
      state.intendedRoute = null
    },
  },
  extraReducers: (builder) => {
    // Handle clinic loading states
    builder
      .addCase(loadAvailableClinics.pending, (state) => {
        state.clinicsLoading = true
      })
      .addCase(loadAvailableClinics.fulfilled, (state, action) => {
        state.clinicsLoading = false
        state.availableClinics = action.payload.availableClinics
        
        // Auto-select first clinic with HATEOAS links
        if (action.payload.firstClinicWithLinks && !state.currentClinic) {
          state.currentClinic = action.payload.firstClinicWithLinks
        }
      })
      .addCase(loadAvailableClinics.rejected, (state, action) => {
        state.clinicsLoading = false
        console.error('Failed to load clinics:', action.error)
      })
  },
})

export const { login, logout, selectClinic, setIntendedRoute, clearIntendedRoute } = authSlice.actions

// HATEOAS-aware selectors
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated
export const selectCurrentClinic = (state: { auth: AuthState }) => state.auth.currentClinic
export const selectAvailableClinics = (state: { auth: AuthState }) => state.auth.availableClinics
export const selectClinicsLoading = (state: { auth: AuthState }) => state.auth.clinicsLoading
export const selectCurrentUser = (state: { auth: AuthState }) => state.auth.user
export const selectIntendedRoute = (state: { auth: AuthState }) => state.auth.intendedRoute

// Convenience selectors for HATEOAS navigation
export const selectCurrentClinicId = (state: { auth: AuthState }) => state.auth.currentClinic?.id || null
export const selectPatientsHref = (state: { auth: AuthState }) => state.auth.currentClinic?.patients.href
export const selectOrdersHref = (state: { auth: AuthState }) => state.auth.currentClinic?.orders.href
export const selectInvoicesHref = (state: { auth: AuthState }) => state.auth.currentClinic?.invoices.href
export const selectDashboardHref = (state: { auth: AuthState }) => state.auth.currentClinic?.dashboard?.href
export const selectReportHref = (state: { auth: AuthState }) => state.auth.currentClinic?.report?.href

export default authSlice.reducer